import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Menu, Moon, Settings as SettingsIcon, Sun, X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDarkMode } from '../../contexts/DarkModeContext';

const Navbar: React.FC = () => {
  const [menuOpened, setMenuOpened] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { authState, logout } = useAuth();
  const { darkModeState, toggleDarkMode } = useDarkMode();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile sheet whenever the route changes.
  useEffect(() => setMenuOpened(false), [location.pathname]);

  const links = [
    { to: '/home', label: 'Home' },
    ...(authState.user ? [{ to: '/messages', label: 'Messages' }] : []),
    { to: '/content', label: 'Syllabus' },
    { to: '/team', label: 'Team' },
    { to: '/contact', label: 'Contact' },
  ];

  const linkClass = (to: string) => {
    const active = location.pathname === to;
    return `relative rounded-lg px-3 py-2 text-sm font-medium transition ${
      active
        ? 'text-base-content'
        : 'text-base-content/55 hover:text-base-content hover:bg-base-content/[0.05]'
    }`;
  };

  const handleLogout = () => {
    const done = () => { window.location.href = '/'; };
    logout(done, done);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'border-b border-ink-900/[0.08] bg-base-100/80 backdrop-blur-xl dark:border-white/10'
          : 'border-b border-transparent bg-base-100/0'
      }`}
    >
      <nav className="section-shell flex h-16 items-center gap-3">
        <Link to="/" className="flex items-center gap-2.5 pr-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-soft">
            T
          </span>
          <span className="text-[17px] font-bold tracking-tight">Tütr.ai</span>
        </Link>

        <ul className="ml-4 hidden items-center gap-0.5 lg:flex">
          {links.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className={linkClass(link.to)}>
                {link.label}
                {location.pathname === link.to && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-brand-600 dark:bg-brand-400"
                  />
                )}
              </Link>
            </li>
          ))}
        </ul>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={toggleDarkMode}
            aria-label={darkModeState.darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            className="grid h-9 w-9 place-items-center rounded-lg text-base-content/60 transition hover:bg-base-content/[0.06] hover:text-base-content"
          >
            {darkModeState.darkMode
              ? <Sun className="h-[18px] w-[18px]" />
              : <Moon className="h-[18px] w-[18px]" />}
          </button>

          {!authState.isAuthenticated ? (
            <div className="hidden items-center gap-2 sm:flex">
              <Link
                to="/authenticate"
                className="rounded-lg px-3.5 py-2 text-sm font-semibold text-base-content/70 transition hover:bg-base-content/[0.06] hover:text-base-content"
              >
                Log in
              </Link>
              <Link
                to="/authenticate?mode=signup"
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-700 active:scale-[0.98]"
              >
                Get started
              </Link>
            </div>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Link
                to="/settings"
                aria-label="Settings"
                className="grid h-9 w-9 place-items-center rounded-lg text-base-content/60 transition hover:bg-base-content/[0.06] hover:text-base-content"
              >
                <SettingsIcon className="h-[18px] w-[18px]" />
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-lg border border-ink-900/[0.12] px-3.5 py-2 text-sm font-semibold transition hover:bg-base-200 dark:border-white/15"
              >
                Sign out
              </button>
            </div>
          )}

          <button
            type="button"
            aria-label="Toggle menu"
            aria-expanded={menuOpened}
            onClick={() => setMenuOpened((open) => !open)}
            className="grid h-9 w-9 place-items-center rounded-lg text-base-content/70 transition hover:bg-base-content/[0.06] lg:hidden"
          >
            {menuOpened ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpened && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-ink-900/[0.08] bg-base-100 lg:hidden dark:border-white/10"
          >
            <ul className="section-shell space-y-1 py-4">
              {links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`block rounded-lg px-3 py-2.5 text-[15px] font-medium ${
                      location.pathname === link.to
                        ? 'bg-brand-600/10 text-brand-600 dark:text-brand-400'
                        : 'text-base-content/70'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li className="flex gap-2 pt-3">
                {!authState.isAuthenticated ? (
                  <>
                    <Link
                      to="/authenticate"
                      className="flex-1 rounded-lg border border-ink-900/[0.12] py-2.5 text-center text-sm font-semibold dark:border-white/15"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/authenticate?mode=signup"
                      className="flex-1 rounded-lg bg-brand-600 py-2.5 text-center text-sm font-semibold text-white"
                    >
                      Get started
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/settings"
                      className="flex-1 rounded-lg border border-ink-900/[0.12] py-2.5 text-center text-sm font-semibold dark:border-white/15"
                    >
                      Settings
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex-1 rounded-lg bg-base-200 py-2.5 text-center text-sm font-semibold"
                    >
                      Sign out
                    </button>
                  </>
                )}
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
